import React, { Component } from 'react';
import PropTypes from 'prop-types';
import config from 'cfg';
import styled from 'styled-components';
import { MenuItem, Tabs, Tab } from 'material-ui';
import { connect } from 'react-redux';
import { reduxForm, Field, FormSection, formValueSelector } from 'redux-form';
import { SelectField, TextField } from 'redux-form-material-ui';
import { compose, getContext } from 'recompose';
import humanize from 'humanize-string';
import { RMessageBuilder } from './MessageBuilder';
import { EventType, TournamentType, IncidentType } from '../types';
import { presence } from 'dash-front/helpers/validation';

const TypeWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 40px;
    margin-top: -5px;
`;

const SectionWrapper = styled.div`
    margin-top: 30px;
`;

const TYPES = {
    EVENT: { type: EventType, label: 'Event', name: 'event' },
    TOURNAMENT: {
        type: TournamentType,
        label: 'Tournament',
        name: 'tournament',
    },
    INCIDENT: { type: IncidentType, label: 'Incident', name: 'incident' },
};

const TRIGGER_TYPES = {
    ENET_EVENT_STATUS: 'enet_event_status',
    ENET_EVENT_INCIDENT: 'enet_event_incident',
    TOURN_MATCH_DAY: 'tourn_match_day',
    TOURN_ROUND_STARTS: 'tourn_round_starts',
    TOURN_ROUND_ENDS: 'tourn_round_ends',
    EVENT_STARTS: 'event_starts',
    EVENT_HALFTIME: 'event_halftime',
    EVENT_FINISHED: 'event_finished',
    EVENT_TEST: 'event_test',
};

const TYPES_BY_TRIGGER = {
    [TRIGGER_TYPES.TOURN_MATCH_DAY]: [TYPES.TOURNAMENT],
    [TRIGGER_TYPES.TOURN_ROUND_STARTS]: [TYPES.TOURNAMENT, TYPES.EVENT],
    [TRIGGER_TYPES.TOURN_ROUND_ENDS]: [TYPES.TOURNAMENT, TYPES.EVENT],
    [TRIGGER_TYPES.EVENT_STARTS]: [TYPES.TOURNAMENT, TYPES.EVENT],
    [TRIGGER_TYPES.EVENT_HALFTIME]: [TYPES.TOURNAMENT, TYPES.EVENT],
    [TRIGGER_TYPES.EVENT_FINISHED]: [TYPES.TOURNAMENT, TYPES.EVENT],
    [TRIGGER_TYPES.EVENT_TEST]: [TYPES.TOURNAMENT, TYPES.EVENT],
    [TRIGGER_TYPES.ENET_EVENT_STATUS]: [TYPES.TOURNAMENT, TYPES.EVENT],
    [TRIGGER_TYPES.ENET_EVENT_INCIDENT]: [TYPES.TOURNAMENT, TYPES.EVENT, TYPES.INCIDENT],
};

const SUBTYPES = {
    [TRIGGER_TYPES.ENET_EVENT_INCIDENT]: [
        { label: 'All', value: '*' },
        { label: 'Goal', value: 'goal' },
        { label: 'Yellow Card', value: 'yellow_card' },
        { label: 'Red Card', value: 'red_card' },
        { label: 'Substitution', value: 'substitution' },
        { label: 'Assist', value: 'assist' },
    ],
    [TRIGGER_TYPES.ENET_EVENT_STATUS]: [
        { label: 'Any', value: '*' },
        { label: 'Match started', value: '1st_half' },
        { label: 'Halftime', value: 'halftime' },
        { label: '2nd half started', value: '2nd_half' },
        { label: 'Finished', value: 'finished' },
    ],
};

const hasOdds = trigger => trigger && trigger !== TRIGGER_TYPES.TOURN_MATCH_DAY;

const triggerTypesOrdered = Object.keys(TRIGGER_TYPES)
    .map(key => TRIGGER_TYPES[key])
    .sort((a, b) => a - b);

const MessageBuilderWithTypes = compose(
    getContext({
        _reduxForm: PropTypes.object,
    }),
    connect((state, { _reduxForm: { form } }) => {
        const trigger = formValueSelector(form)(state, 'trigger');
        return {
            types: TYPES_BY_TRIGGER[trigger],
            withOdds: hasOdds(trigger),
        };
    }),
)(({ types, ...props }) => <Field {...props} types={types} component={RMessageBuilder} />);

const SubtypeField = compose(
    getContext({
        _reduxForm: PropTypes.object,
    }),
    connect((state, { _reduxForm: { form } }) => ({
        subtypes: SUBTYPES[formValueSelector(form)(state, 'trigger')],
    })),
)(({ subtypes, ...props }) =>
    Array.isArray(subtypes) ? (
        <div>
            <Field {...props} component={SelectField}>
                {subtypes.map(el => (
                    <MenuItem key={el.value} value={el.value} primaryText={el.label} />
                ))}
            </Field>
        </div>
    ) : null,
);

const LANGUAGES = {
    en: 'English',
    da: 'Danish',
    no: 'Norwegian',
    de: 'Deutsch',
    fi: 'Finish',
    th: 'Thai',
    se: 'Swedish',
    sc: 'Chinise simpl',
    tc: 'Chinise trad',
};

const validateTabs = (val, { template }) => {
    if (template) {
        for (const tpl in template) {
            if (!template[tpl]) continue;
            else return undefined;
        }
    }
    return 'Required';
};

class MessageForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            activeTab: this.props.lang,
        };
    }

    onTriggerChange = () => {
        this.props.change('subtype', null);
    };

    handleTabChange = activeTab => {
        this.setState({ activeTab });
    };

    render() {
        const { activeTab } = this.state;
        const supportedLanguages = config.supportedLanguages;

        return (
            <div>
                <div>
                    <Field name="title" floatingLabelText="Title" component={TextField} validate={presence} fullWidth />
                </div>
                <TypeWrapper>
                    <Field
                        name="trigger"
                        floatingLabelText="Trigger type"
                        onChange={this.onTriggerChange}
                        component={SelectField}
                        validate={presence}
                        fullWidth
                    >
                        {triggerTypesOrdered.map(trigger => (
                            <MenuItem key={trigger} value={trigger} primaryText={humanize(trigger)} />
                        ))}
                    </Field>
                    <SubtypeField name="subtype" floatingLabelText="Subtype" validate={presence} fullWidth />
                </TypeWrapper>
                <SectionWrapper>
                    <FormSection name="template">
                        <Tabs value={activeTab} onChange={this.handleTabChange}>
                            {supportedLanguages.map(lang => (
                                <Tab label={LANGUAGES[lang] || lang} value={lang} key={lang}>
                                    <MessageBuilderWithTypes
                                        name={lang}
                                        validate={validateTabs}
                                        floatingLabelText={`Enter ${LANGUAGES[lang] || lang} message`}
                                    />
                                </Tab>
                            ))}
                        </Tabs>
                    </FormSection>
                </SectionWrapper>
            </div>
        );
    }
}

MessageForm.propTypes = {
    change: PropTypes.func.isRequired,
};

export default connect(state => ({
    lang: state.user.props.lang,
}))(reduxForm({ form: 'messageForm' })(MessageForm));



/** WEBPACK FOOTER **
 ** ../src/dash/front/messages/components/MessageForm.js
 **/